import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./../../../assets/css/student/roadmap/choose.css";

const skillOptions = [
  {
    id: "web",
    label: "Web Development",
    icon: "⚡",
    desc: "HTML, CSS, JS & frameworks",
  },
  {
    id: "app",
    label: "App Development",
    icon: "📱",
    desc: "iOS, Android & cross-platform",
  },
  {
    id: "uiux",
    label: "UI/UX Design",
    icon: "✦",
    desc: "Figma, research & prototyping",
  },
];

const levelOptions = [
  {
    id: "beginner",
    label: "Beginner",
    icon: "🌱",
    desc: "Starting from scratch",
  },
  {
    id: "intermediate",
    label: "Intermediate",
    icon: "🔥",
    desc: "I know the basics",
  },
  { id: "advanced", label: "Advanced", icon: "⚔️", desc: "Ready to go deep" },
];

const goalOptions = [
  { id: "job", label: "Get a Job", icon: "💼", desc: "Land a tech role" },
  {
    id: "freelance",
    label: "Freelancing",
    icon: "🌐",
    desc: "Work independently",
  },
  {
    id: "basics",
    label: "Learn Basics",
    icon: "📚",
    desc: "Build a foundation",
  },
];

const timeOptions = [
  { id: "2h", label: "2 hrs / day", icon: "⏱", desc: "Light & steady" },
  { id: "4h", label: "4 hrs / day", icon: "⚡", desc: "Focused sprint" },
  { id: "flex", label: "Flexible", icon: "🌊", desc: "At your own pace" },
];

const steps = [
  { key: "skill", label: "Skill Type", options: skillOptions },
  { key: "level", label: "Experience Level", options: levelOptions },
  { key: "goal", label: "Your Goal", options: goalOptions },
  { key: "time", label: "Time Commitment", options: timeOptions },
];

function SelectionCard({ option, selected, onSelect }) {
  return (
    <motion.div
      className={`cs-card ${selected ? "cs-card--selected" : ""}`}
      onClick={() => onSelect(option.id)}
      whileHover={{ y: -3, boxShadow: "0 12px 32px rgba(120,28,46,0.13)" }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
    >
      <span className="cs-card__icon">{option.icon}</span>
      <span className="cs-card__label">{option.label}</span>
      <span className="cs-card__desc">{option.desc}</span>
      {selected && (
        <motion.span
          className="cs-card__check"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 18 }}
        >
          ✓
        </motion.span>
      )}
    </motion.div>
  );
}

export default function ChooseSkill({ onContinue }) {
  const [selections, setSelections] = useState({
    skill: null,
    level: null,
    goal: null,
    time: null,
  });
  const navigate = useNavigate();

  const allSelected = Object.values(selections).every(Boolean);

  const handleSelect = (key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  const handleContinue = () => {
    if (allSelected && onContinue) onContinue(selections);
    navigate("/roadmaps");
  };

  const completedCount = Object.values(selections).filter(Boolean).length;

  return (
    <div className="cs-root">
      <div className="cs-bg-texture" />

      <motion.div
        className="cs-container"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Header */}
        <header className="cs-header">
          <div className="cs-logo">
            <span className="cs-logo__dot" />
            <span className="cs-logo__name">Pathwise</span>
          </div>
          <div className="cs-progress-track">
            <div
              className="cs-progress-fill"
              style={{ width: `${(completedCount / 4) * 100}%` }}
            />
          </div>
          <p className="cs-progress-label">{completedCount} of 4 selected</p>
        </header>

        {/* Title */}
        <div className="cs-title-block">
          <h1 className="cs-title">Build Your Personalized Roadmap</h1>
          <p className="cs-subtitle">
            Tell us about your goals so we can tailor your learning path
          </p>
        </div>

        {/* Step Sections */}
        <div className="cs-steps">
          {steps.map((step, idx) => (
            <motion.section
              key={step.key}
              className="cs-step"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
            >
              <div className="cs-step__header">
                <span className="cs-step__number">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h2 className="cs-step__label">{step.label}</h2>
              </div>
              <div className="cs-cards-grid">
                {step.options.map((opt) => (
                  <SelectionCard
                    key={opt.id}
                    option={opt}
                    selected={selections[step.key] === opt.id}
                    onSelect={(id) => handleSelect(step.key, id)}
                  />
                ))}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Continue Button */}
        <div className="cs-footer">
          <motion.button
            className={`cs-btn ${allSelected ? "cs-btn--active" : "cs-btn--disabled"}`}
            onClick={handleContinue}
            disabled={!allSelected}
            whileHover={allSelected ? { scale: 1.02 } : {}}
            whileTap={allSelected ? { scale: 0.97 } : {}}
          >
            <span>Generate My Roadmap</span>
            <span className="cs-btn__arrow">→</span>
          </motion.button>
          {!allSelected && (
            <p className="cs-hint">Complete all sections to continue</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
